import React, { useMemo, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { DataGrid } from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { colors } from "../../../Shared/colors";
import { Loader } from "../../../Shared/Loader";
import { Card, StatusMeta, PrimaryButton, GhostButton } from "./socialLeadsStyles";
import { SocialLeadsAnswersDrawer } from "./SocialLeadsAnswersDrawer";
import {
  formatDuration,
  responseTone,
  rowsToCsv,
  downloadCsv,
} from "./socialLeadsFormat";

/**
 * Reconciliation table: one row per Meta submission, showing what the CRM
 * knows about it. Rows already in the CRM are deliberately quiet; the two
 * states that need a human — not imported, failed — carry a coloured stripe
 * and an action, so the work stands out without having to read every row.
 */
export const SocialLeadsDataTable = ({
  rows = [],
  isFetching,
  selection,
  onSelectionChange,
  onImport,
  isImporting,
  pageSize,
  onPageSizeChange,
  error,
  slaMinutes = 60,
  attention = "",
  onClearAttention,
}) => {
  // Which row's answers are open. Held here rather than lifted: nothing outside
  // the table cares which row is being inspected.
  const [openRow, setOpenRow] = useState(null);

  const columns = useMemo(
    () => [
      {
        field: "status",
        headerName: "Status",
        width: 140,
        renderCell: (params) => {
          const meta = StatusMeta[params.row.status];
          if (!meta) return <span>{params.row.status}</span>;
          return (
            <Pill $color={meta.color} $bg={meta.bg}>
              {meta.label}
            </Pill>
          );
        },
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 170,
        renderCell: (params) => (
          <Stacked>
            <span className="primary">
              {params.row.name || "—"}
              {params.row.isExistingClient ? (
                <Tooltip title="We already had this person on file when they enquired — this lead went to their existing owner rather than through round-robin">
                  <ReturningBadge>Returning</ReturningBadge>
                </Tooltip>
              ) : null}
            </span>
            {params.row.error ? (
              <Tooltip title={params.row.error}>
                <span className="error">{params.row.error}</span>
              </Tooltip>
            ) : null}
          </Stacked>
        ),
      },
      {
        field: "phone",
        headerName: "Phone",
        width: 155,
        renderCell: (params) => (
          <Numeric>{params.row.phone || "—"}</Numeric>
        ),
      },
      {
        field: "formName",
        headerName: "Form / Project",
        flex: 1,
        minWidth: 180,
        renderCell: (params) => (
          <Stacked>
            <span className="primary">
              {params.row.projectName || params.row.formName || "—"}
            </span>
            {params.row.formName && params.row.projectName ? (
              <span className="sub">{params.row.formName}</span>
            ) : null}
          </Stacked>
        ),
      },
      {
        field: "answers",
        headerName: "Answers",
        width: 132,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const answers = params.row.answers || [];
          if (!answers.length) return <Muted>—</Muted>;
          // The values themselves vary wildly in length between forms, so the
          // cell carries the count and the hover carries the detail. The full
          // set is one click away in the drawer.
          const preview = answers
            .map((answer) => `${answer.label}: ${answer.value}`)
            .join("\n");
          return (
            <Tooltip title={<PreviewTip>{preview}</PreviewTip>} placement="left">
              <AnswerChip>
                {answers.length} {answers.length === 1 ? "answer" : "answers"}
              </AnswerChip>
            </Tooltip>
          );
        },
      },
      {
        field: "submittedAt",
        headerName: "Submitted on Meta",
        width: 165,
        renderCell: (params) => {
          if (!params.row.submittedAt) return <span>—</span>;
          const when = moment(params.row.submittedAt);
          return (
            <Stacked>
              <Numeric>{when.format("DD MMM, HH:mm")}</Numeric>
              <span className="sub">{when.fromNow()}</span>
            </Stacked>
          );
        },
      },
      {
        field: "minutesToFirstContact",
        headerName: "First contact",
        width: 140,
        renderCell: (params) => {
          const { minutesToFirstContact, minutesWaiting, status } = params.row;
          if (status !== "inCrm") return <Muted>—</Muted>;

          const tone = responseTone({
            minutesToFirstContact,
            minutesWaiting,
            slaMinutes,
          });

          if (tone === "ok" || tone === "late") {
            return (
              <Stacked>
                <ResponseValue $tone={tone}>
                  {formatDuration(minutesToFirstContact)}
                </ResponseValue>
                <span className="sub">after submission</span>
              </Stacked>
            );
          }

          // Nobody has called yet — show how long they have been waiting, since
          // that is the number someone can still do something about.
          return (
            <Stacked>
              <ResponseValue $tone={tone}>
                {tone === "overdue" ? "Waiting" : "Not yet"}
              </ResponseValue>
              <span className="sub">{formatDuration(minutesWaiting)} so far</span>
            </Stacked>
          );
        },
      },
      {
        field: "assignedTo",
        headerName: "Assigned to",
        width: 150,
        renderCell: (params) =>
          params.row.assignedTo?.name ? (
            <span>{params.row.assignedTo.name}</span>
          ) : (
            <Muted>Not yet assigned</Muted>
          ),
      },
      {
        field: "leadID",
        headerName: "Lead ID",
        width: 105,
        renderCell: (params) =>
          params.row.leadID ? (
            <LeadId>{params.row.leadID}</LeadId>
          ) : (
            <Muted>—</Muted>
          ),
      },
      {
        field: "action",
        headerName: "Action",
        width: 115,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          if (params.row.status === "inCrm") return <Muted>—</Muted>;
          const isRetry = params.row.status === "failed";
          return (
            <RowButton
              type="button"
              $retry={isRetry}
              disabled={isImporting}
              onClick={(event) => {
                event.stopPropagation();
                onImport([params.row.id]);
              }}
            >
              {isRetry ? "Retry" : "Import"}
            </RowButton>
          );
        },
      },
    ],
    [onImport, isImporting, slaMinutes]
  );

  // Rows in the CRM need no attention, so they carry no stripe.
  const getRowClassName = (params) =>
    params.row.status === "inCrm" ? "" : `row-${params.row.status}`;

  const selectedCount = selection.length;

  if (error) {
    return (
      <Card>
        <Empty>
          <InboxOutlinedIcon />
          <h3>Couldn't load submissions from Meta</h3>
          <p>
            The lead history couldn't be fetched just now. Check the connection
            to Meta and try again.
          </p>
        </Empty>
      </Card>
    );
  }

  return (
    <Card>
      {attention ? (
        <AttentionBar $tone={attention}>
          <AccessTimeOutlinedIcon />
          <p>
            {attention === "overdue" ? (
              <>
                <strong>{rows.length}</strong>{" "}
                {rows.length === 1 ? "lead" : "leads"} nobody has called, waiting
                over {formatDuration(slaMinutes)} — longest wait first.
              </>
            ) : (
              <>
                <strong>{rows.length}</strong>{" "}
                {rows.length === 1 ? "lead" : "leads"} not contacted yet, still
                inside {formatDuration(slaMinutes)} — longest wait first.
              </>
            )}
          </p>
          {onClearAttention ? (
            <button type="button" onClick={onClearAttention}>
              <CloseOutlinedIcon />
              Show all submissions
            </button>
          ) : null}
        </AttentionBar>
      ) : null}

      <TableHead>
        <span className="count">
          Showing <strong>{rows.length}</strong>{" "}
          {rows.length === 1 ? "submission" : "submissions"}
        </span>
        <GhostButton
          type="button"
          disabled={!rows.length}
          onClick={() =>
            downloadCsv(
              `meta-leads-${moment().format("YYYY-MM-DD")}.csv`,
              rowsToCsv(rows)
            )
          }
        >
          <FileDownloadOutlinedIcon />
          Export CSV
        </GhostButton>
      </TableHead>

      {selectedCount > 0 && (
        <BulkBar>
          <span className="n">
            <strong>{selectedCount}</strong> selected
          </span>
          <PrimaryButton
            type="button"
            disabled={isImporting}
            onClick={() => onImport(selection)}
          >
            <DownloadOutlinedIcon />
            {isImporting ? "Importing…" : "Import into CRM"}
          </PrimaryButton>
          <GhostButton type="button" onClick={() => onSelectionChange([])}>
            Clear selection
          </GhostButton>
        </BulkBar>
      )}

      <GridWrap>
        <DataGrid
          rows={rows}
          columns={columns}
          checkboxSelection
          disableSelectionOnClick
          loading={isFetching}
          selectionModel={selection}
          onSelectionModelChange={onSelectionChange}
          isRowSelectable={(params) => params.row.status !== "inCrm"}
          onRowClick={(params) => setOpenRow(params.row)}
          getRowClassName={getRowClassName}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          rowsPerPageOptions={[25, 50, 100]}
          components={{
            NoRowsOverlay: () =>
              attention ? (
                <Empty>
                  <InboxOutlinedIcon />
                  <h3>
                    {attention === "overdue"
                      ? "Nothing is overdue"
                      : "Nothing is waiting"}
                  </h3>
                  <p>
                    Every lead in this date range has been contacted
                    {attention === "overdue"
                      ? ` within ${formatDuration(slaMinutes)}.`
                      : "."}{" "}
                    These may have been called since the page last synced.
                  </p>
                </Empty>
              ) : (
                <Empty>
                  <InboxOutlinedIcon />
                  <h3>Nothing matches these filters</h3>
                  <p>
                    No Meta submissions in this date range with that status.
                    Widen the dates, or set status back to All.
                  </p>
                </Empty>
              ),
          }}
          sx={{
            border: "none",
            fontSize: "0.82rem",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.lightWhite,
              borderBottom: `1px solid ${colors.inputBorderWhite}`,
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "0.68rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.6px",
              color: colors.textLight,
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${colors.inputBorderWhite}`,
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: `2px solid ${colors.brown}`,
              outlineOffset: "-2px",
            },
            "& .MuiCheckbox-root.Mui-checked": { color: colors.brown },
            // The whole row opens the answers panel, so it has to look clickable.
            "& .MuiDataGrid-row": { cursor: "pointer" },
            "& .MuiDataGrid-row:hover": { backgroundColor: colors.lightWhite },
            "& .MuiDataGrid-row.row-notImported": {
              boxShadow: `inset 3px 0 0 0 ${StatusMeta.notImported.color}`,
            },
            "& .MuiDataGrid-row.row-failed": {
              boxShadow: `inset 3px 0 0 0 ${StatusMeta.failed.color}`,
            },
          }}
        />
        {isFetching ? <Loader /> : null}
      </GridWrap>

      <SocialLeadsAnswersDrawer
        row={openRow}
        onClose={() => setOpenRow(null)}
        isImporting={isImporting}
        slaMinutes={slaMinutes}
        onImport={(ids) => {
          setOpenRow(null);
          onImport(ids);
        }}
      />
    </Card>
  );
};

/**
 * States, at the top of the table, that this is a narrowed view and how to
 * leave it. Without it the drill-down looks identical to a table that simply
 * has few rows, and someone will read six leads as the whole week's enquiries.
 */
const AttentionBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 0.85rem;
  padding: 11px 18px;
  border-bottom: 1px solid ${colors.inputBorderWhite};
  background: ${(props) =>
    props.$tone === "overdue" ? "rgba(232, 11, 11, 0.06)" : colors.inputWhite};
  box-shadow: inset 3px 0 0 0
    ${(props) => (props.$tone === "overdue" ? colors.veryHot : colors.textLight)};

  > svg {
    font-size: 1.15rem;
    flex-shrink: 0;
    color: ${(props) =>
      props.$tone === "overdue" ? colors.veryHot : colors.textMed};
  }

  p {
    margin: 0;
    flex: 1;
    min-width: 200px;
    font-size: 0.82rem;
    color: ${colors.textMed};
  }
  p strong {
    font-variant-numeric: tabular-nums;
    color: ${(props) =>
      props.$tone === "overdue" ? colors.veryHot : colors.text};
  }

  button {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-family: inherit;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 6px 12px;
    min-height: 32px;
    border-radius: 7px;
    cursor: pointer;
    white-space: nowrap;
    color: ${colors.textMed};
    background: ${colors.white};
    border: 1px solid ${colors.inputBorderWhite};
    transition: all 0.18s ease;

    svg {
      font-size: 0.9rem;
    }
    &:hover {
      border-color: ${colors.brown};
      color: ${colors.brown};
    }
    &:focus-visible {
      outline: 2px solid ${colors.brown};
      outline-offset: 2px;
    }
  }
`;

const TableHead = styled.div`
  padding: 13px 18px;
  border-bottom: 1px solid ${colors.inputBorderWhite};
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;

  .count {
    font-size: 0.85rem;
    font-weight: 600;
    color: ${colors.text};
  }
  .count strong {
    color: ${colors.brown};
    font-variant-numeric: tabular-nums;
  }
`;

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 11px 18px;
  background: ${colors.inputWhite};
  border-bottom: 1px solid ${colors.inputBorderWhite};

  .n {
    font-size: 0.82rem;
    font-weight: 600;
    color: ${colors.text};
  }
  .n strong {
    color: ${colors.brown};
    font-variant-numeric: tabular-nums;
  }
`;

const GridWrap = styled.div`
  height: 62vh;
  min-height: 380px;
  position: relative;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 20px;
  white-space: nowrap;
  color: ${(props) => props.$color};
  background: ${(props) => props.$bg};

  &::before {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const Stacked = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  line-height: 1.35;

  .primary {
    font-weight: 600;
    color: ${colors.text};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sub {
    font-size: 0.7rem;
    color: ${colors.textLight};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .error {
    font-size: 0.7rem;
    color: ${colors.veryHot};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Numeric = styled.span`
  font-variant-numeric: tabular-nums;
`;

const Muted = styled.span`
  color: ${colors.textLight};
`;

const ReturningBadge = styled.span`
  margin-left: 6px;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 1px 6px;
  border-radius: 20px;
  vertical-align: middle;
  color: ${colors.purple};
  background: rgba(126, 87, 194, 0.1);
  border: 1px solid rgba(126, 87, 194, 0.22);
`;

const ResponseValue = styled.span`
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(props) =>
    props.$tone === "overdue" || props.$tone === "late"
      ? colors.veryHot
      : props.$tone === "ok"
      ? colors.text
      : colors.textMed};
`;

const AnswerChip = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 20px;
  white-space: nowrap;
  color: ${colors.textBlue};
  background: rgba(45, 122, 244, 0.09);
  border: 1px solid rgba(45, 122, 244, 0.18);
`;

const PreviewTip = styled.span`
  display: block;
  white-space: pre-line;
  font-size: 0.74rem;
  line-height: 1.55;
`;

const LeadId = styled.span`
  font-weight: 600;
  color: ${colors.brown};
  font-variant-numeric: tabular-nums;
`;

const RowButton = styled.button`
  font-family: inherit;
  font-size: 0.73rem;
  font-weight: 600;
  padding: 5px 13px;
  min-height: 30px;
  border-radius: 7px;
  cursor: pointer;
  background: ${colors.white};
  transition: all 0.18s ease;
  border: 1px solid
    ${(props) => (props.$retry ? colors.veryHot : colors.brown)};
  color: ${(props) => (props.$retry ? colors.veryHot : colors.brown)};

  &:hover:not(:disabled) {
    background: ${(props) => (props.$retry ? colors.veryHot : colors.brown)};
    color: ${colors.white};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid ${colors.textBlue};
    outline-offset: 2px;
  }
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  padding: 48px 24px;

  svg {
    font-size: 2.2rem;
    color: ${colors.textLight};
    margin-bottom: 10px;
  }
  h3 {
    margin: 0 0 6px 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: ${colors.text};
  }
  p {
    margin: 0;
    font-size: 0.82rem;
    color: ${colors.textMed};
    max-width: 46ch;
  }
`;
